import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";

import { exhaustiveSwitch, hasOwnProperty } from "replo-utils/lib/misc";
import {
  knownMcpServerMetadataRegistries,
  knownMcpToolMetadataRegistry,
} from "schemas/agentTools/mcpServers";

import { normalizeIdentifier } from "../shared";
import { getBedrockMcpServerToolDisplay } from "./bedrock";
import { getNextDevtoolsMcpServerToolDisplay } from "./next-devtools";
import { getParallelSearchMcpServerToolDisplay } from "./parallel-search";

type KnownMcpToolNamesOf<
  ServerName extends keyof typeof knownMcpServerMetadataRegistries,
> = Extract<
  keyof (typeof knownMcpServerMetadataRegistries)[ServerName],
  string
>;

type KnownMcpToolMatchByServerName<ServerName extends KnownMcpServerName> = {
  serverName: ServerName;
  toolName: KnownMcpToolNamesOf<ServerName>;
};

type KnownMcpToolMatch = {
  [ServerName in keyof typeof knownMcpServerMetadataRegistries]: KnownMcpToolMatchByServerName<ServerName>;
}[keyof typeof knownMcpServerMetadataRegistries];

type KnownMcpToolMetadata =
  (typeof knownMcpToolMetadataRegistry)[keyof typeof knownMcpToolMetadataRegistry];

type KnownMcpServerName = KnownMcpToolMatch["serverName"];

const knownMcpServerNames = [
  "bedrock",
  "next-devtools",
  "parallel-search",
] as const satisfies readonly KnownMcpServerName[];

function hasKnownToolName<Registry extends object>(
  metadataRegistry: Registry,
  toolName: string,
): toolName is Extract<keyof Registry, string> {
  return hasOwnProperty(metadataRegistry, toolName);
}

function getMcpToolMetadata({
  toolName,
}: {
  toolName: string;
}): KnownMcpToolMetadata | null {
  return hasKnownToolName(knownMcpToolMetadataRegistry, toolName)
    ? knownMcpToolMetadataRegistry[toolName]
    : null;
}

function getKnownMcpToolMatchesByServerName<
  ServerName extends KnownMcpServerName,
>({
  serverName,
  metadataRegistry,
}: {
  serverName: ServerName;
  metadataRegistry: (typeof knownMcpServerMetadataRegistries)[ServerName];
}) {
  const knownMcpToolMatchesByIdentifier: Record<
    string,
    KnownMcpToolMatchByServerName<ServerName>
  > = {};

  for (const knownToolName of Object.keys(metadataRegistry)) {
    if (!hasKnownToolName(metadataRegistry, knownToolName)) {
      continue;
    }

    const normalizedKnownToolName = normalizeIdentifier({
      value: knownToolName,
    });
    const knownToolMatch: KnownMcpToolMatchByServerName<ServerName> = {
      serverName,
      toolName: knownToolName,
    };
    knownMcpToolMatchesByIdentifier[normalizedKnownToolName] = knownToolMatch;
    knownMcpToolMatchesByIdentifier[
      `${normalizeIdentifier({ value: serverName })}_${normalizedKnownToolName}`
    ] = knownToolMatch;
  }

  return knownMcpToolMatchesByIdentifier;
}

const knownMcpToolMatchesByIdentifier: Record<string, KnownMcpToolMatch> = {};

for (const serverName of knownMcpServerNames) {
  Object.assign(
    knownMcpToolMatchesByIdentifier,
    getKnownMcpToolMatchesByServerName({
      serverName,
      metadataRegistry: knownMcpServerMetadataRegistries[serverName],
    }),
  );
}

export function getMcpToolMatch({ toolName }: { toolName: string }) {
  const normalizedToolName = normalizeIdentifier({ value: toolName });
  const directMatch = knownMcpToolMatchesByIdentifier[normalizedToolName];
  if (directMatch) {
    return directMatch;
  }

  for (const serverName of knownMcpServerNames) {
    const serverPrefix = `${normalizeIdentifier({ value: serverName })}_`;
    if (!normalizedToolName.startsWith(serverPrefix)) {
      continue;
    }

    const unprefixedToolName = normalizedToolName.slice(serverPrefix.length);
    const unprefixedMatch = knownMcpToolMatchesByIdentifier[unprefixedToolName];
    if (unprefixedMatch?.serverName === serverName) {
      return unprefixedMatch;
    }
  }

  return null;
}

export function getMcpToolIconKey({ toolName }: { toolName: string }) {
  const toolMatch = getMcpToolMatch({ toolName });
  if (!toolMatch) {
    return null;
  }

  return (
    getMcpToolMetadata({ toolName: toolMatch.toolName })?.shape.iconKey.value ??
    null
  );
}

export function getMcpToolDisplay({
  toolName,
  toolPart,
  toolParts,
}: {
  toolName: string;
  toolPart: OpencodeToolPart;
  toolParts: readonly OpencodeToolPart[];
}) {
  const toolMatch = getMcpToolMatch({ toolName });
  if (!toolMatch) {
    return null;
  }

  return exhaustiveSwitch(
    toolMatch,
    "serverName",
  )({
    bedrock: ({ toolName: matchedToolName }) => {
      return getBedrockMcpServerToolDisplay({
        toolName: matchedToolName,
        toolPart,
        toolParts,
      });
    },
    "next-devtools": ({ toolName: matchedToolName }) => {
      return getNextDevtoolsMcpServerToolDisplay({
        toolName: matchedToolName,
        toolPart,
      });
    },
    "parallel-search": ({ toolName: matchedToolName }) => {
      return getParallelSearchMcpServerToolDisplay({
        toolName: matchedToolName,
        toolPart,
      });
    },
  });
}
